// Publieke volgpagina /Volgen (sessie 134): "Volg je bestelling of
// retour". Eén zoekscherm voor order- (ORD…) en retournummers (RET-…)
// plus e-mailadres. Indeling: door JP goedgekeurde mock van 27-10
// (Brain grote-ronde/mock-volgen-bestelling-of-retour-2026-10-27):
// lichtblauw voor gedane stappen, een oranje ring voor de huidige stap.
//
// Maten (sessie 136, JP 27-10): de retourpagina is de enige maat. Kop,
// tekst, velden, knop en kaarten gebruiken dezelfde componenten en
// klassen als RetourPagina en RetourShopElementen; geen eigen maten.
//
// Opbouw (sessie 137, JP 27-10): ook de indeling volgt /retour. USP-balk
// onder de kop, hetzelfde raster met links het formulier of de kaarten
// en rechts een uitlegkaart in de stijl van het retourbeleid. Geen
// aparte hulpkaart: kop en zwevende chatknop volstaan (JP 27-10).
//
// Eerlijk boven mooi: de pagina toont alleen wat de backend weet. Er
// wordt nooit een bezorgdag geschat; zonder zending of scan staat er
// een tussenstand in woorden.
//
// De tokenlink uit oude retourmails (/RetourStatus?token=…) komt via
// NaarVolgen hier binnen en toont dezelfde retourweergave als vroeger.

import "@fontsource/nunito-sans/400.css";
import "@fontsource/nunito-sans/600.css";
import "@fontsource/nunito-sans/700.css";
import "@fontsource/arimo/700.css";

import { createElement, useCallback, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Ban,
  CalendarCheck,
  ChevronDown,
  CircleCheck,
  ClipboardCheck,
  ExternalLink,
  Hourglass,
  Loader2,
  MapPin,
  MessageCircle,
  PackageCheck,
  PackageOpen,
  Receipt,
  Send,
  Tag,
  Truck,
  TriangleAlert,
  type LucideIcon,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  retourStatusApi,
  volgenApi,
  type RetourStatusPaginaAntwoord,
  type VolgenAntwoord,
  type VolgenBestelling,
  type VolgenStap,
  type VolgenZending,
} from "@/lib/api";
import {
  openChatWidget,
  RetourFooter,
  RetourHeader,
  RETOUR_KAART,
  RETOUR_KOLOM,
  RETOUR_RASTER,
  UspBalk,
  VinkjesKaart,
} from "./RetourShopElementen";
import {
  ArtikelenKaart,
  RetourGeslotenBlok,
  RetourStatusKop,
  RetourStatusWoord,
  Tijdlijn,
} from "./RetourStatusPagina";

const WHATSAPP_URL = "https://wa.me/31629806571";

// Linkstijl van de retourpagina (kruisverwijzing en beleid-link).
const LINK =
  "rounded-sm font-medium text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

// Missers in deze browsersessie; bij 3 stopt het formulier (de server
// heeft daarnaast zijn eigen rate limit en dagcap).
const MISSER_SLEUTEL = "vo_volgen_missers";
const MAX_MISSERS = 3;

function leesMissers(): number {
  try {
    const waarde = Number(window.sessionStorage.getItem(MISSER_SLEUTEL));
    return Number.isFinite(waarde) ? waarde : 0;
  } catch {
    return 0;
  }
}

function telMisser(): number {
  const nieuw = leesMissers() + 1;
  try {
    window.sessionStorage.setItem(MISSER_SLEUTEL, String(nieuw));
  } catch {
    /* geen sessionStorage (privémodus): alleen de server telt */
  }
  return nieuw;
}

function wisMissers(): void {
  try {
    window.sessionStorage.removeItem(MISSER_SLEUTEL);
  } catch {
    /* geen sessionStorage: niets te wissen */
  }
}

// ── Datums (Nederlands, Europe/Amsterdam) ─────────────────────────

const TIJDZONE = "Europe/Amsterdam";

function geldig(iso: string | null): Date | null {
  if (!iso) return null;
  const datum = new Date(iso);
  return Number.isNaN(datum.getTime()) ? null : datum;
}

/** "zondag 11 oktober" */
function dagTekst(iso: string | null): string {
  const datum = geldig(iso);
  if (!datum) return "";
  return new Intl.DateTimeFormat("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: TIJDZONE,
  }).format(datum);
}

/** "maandag 12 oktober, 15:21" */
function momentTekst(iso: string | null): string {
  const datum = geldig(iso);
  if (!datum) return "";
  const tijd = new Intl.DateTimeFormat("nl-NL", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: TIJDZONE,
  }).format(datum);
  return `${dagTekst(iso)}, ${tijd}`;
}

/** Kalenderdag "2026-10-14" → "woensdag 14 oktober" (middag UTC, zodat
 *  de dag in Amsterdam nooit verschuift). */
function kalenderdagTekst(dag: string | null): string {
  if (!dag || !/^\d{4}-\d{2}-\d{2}$/.test(dag)) return "";
  return dagTekst(`${dag}T12:00:00Z`);
}

// ── Kleine hulpmiddelen ───────────────────────────────────────────

const VERVOERDER_NAMEN: Record<string, string> = {
  postnl: "PostNL",
  dhl: "DHL",
  dpd: "DPD",
  gls: "GLS",
  ups: "UPS",
};

function vervoerderNaam(ruw: string | null): string | null {
  const schoon = (ruw ?? "").trim();
  if (!schoon) return null;
  return (
    VERVOERDER_NAMEN[schoon.toLowerCase()] ??
    schoon.charAt(0).toUpperCase() + schoon.slice(1)
  );
}

function beginKapitaal(tekst: string): string {
  return tekst.charAt(0).toUpperCase() + tekst.slice(1);
}

const STAPPEN: VolgenStap[] = ["besteld", "ingepakt", "onderweg", "bezorgd"];

const STAP_ICOON: Record<VolgenStap, LucideIcon> = {
  besteld: Receipt,
  ingepakt: PackageOpen,
  onderweg: PackageCheck,
  bezorgd: CircleCheck,
};

function retourIcoon(data: RetourStatusPaginaAntwoord): LucideIcon {
  if (data.fase === "gesloten") return Ban;
  const per_status: Record<string, LucideIcon> = {
    aangemeld: ClipboardCheck,
    beoordeling: Hourglass,
    label_aangemaakt: Tag,
    onderweg: Truck,
    ontvangen: PackageOpen,
    afgehandeld: CircleCheck,
  };
  return per_status[data.status] ?? ClipboardCheck;
}

function foutStatus(fout: unknown): number | null {
  const status = (fout as { response?: { status?: unknown } } | null)?.response
    ?.status;
  return typeof status === "number" ? status : null;
}

// ── Doorverwijzing van het oude pad ───────────────────────────────

/** /RetourStatus?token=… uit oude mails → /volgen?token=… */
export function NaarVolgen() {
  const { search } = useLocation();
  return <Navigate to={`/volgen${search}`} replace />;
}

// ── Onderdelen ────────────────────────────────────────────────────

// Alleen nog op "te veel pogingen": daar verwijst de tekst naar chat
// en WhatsApp. Op zoek- en resultaatscherm zijn de portaalkop en de
// zwevende knop "Hulp & chat" al de ingangen (sessie 135).
function HulpStrook() {
  const { t } = useTranslation();
  return (
    <p className="flex flex-wrap items-center gap-2 text-[14px]">
      <MessageCircle className="h-4 w-4 text-brand-cyan" aria-hidden />
      <button type="button" onClick={openChatWidget} className={LINK}>
        {t("volgen.hulp_chat")}
      </button>
      <span aria-hidden className="text-gray-500">
        ·
      </span>
      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={LINK}
      >
        {t("volgen.hulp_whatsapp")}
      </a>
    </p>
  );
}

// Balk, labels en tekstmaten van de StappenIndicator op de retourpagina;
// alleen de kleuren zijn die van de goedgekeurde mock (lichtblauw gedaan,
// oranje ring nu).
function Voortgangslijn({ stap }: { stap: VolgenStap }) {
  const { t } = useTranslation();
  const huidig = STAPPEN.indexOf(stap);
  return (
    <ol
      aria-label={t("volgen.voortgang_label")}
      className="mt-5 grid list-none grid-cols-4"
    >
      {STAPPEN.map((naam, index) => {
        const gedaan = index < huidig;
        const nu = index === huidig;
        const laatste = index === STAPPEN.length - 1;
        return (
          <li
            key={naam}
            aria-current={nu ? "step" : undefined}
            className={laatste ? "text-right" : undefined}
          >
            <span
              aria-hidden
              className={`relative flex h-4 items-center ${laatste ? "justify-end" : ""}`}
            >
              <span
                className={`absolute inset-x-0 block h-[5px] rounded-full ${
                  gedaan ? "bg-brand-cyan" : "bg-muted"
                }`}
              />
              {nu ? (
                <span
                  data-stand="nu"
                  className="relative h-3 w-3 rounded-full bg-white ring-2 ring-brand-orange-600"
                />
              ) : gedaan ? (
                <span
                  data-stand="gedaan"
                  className="relative h-3 w-3 rounded-full bg-brand-cyan"
                />
              ) : (
                <span
                  data-stand="nog_niet"
                  className="relative h-3 w-3 rounded-full border border-input bg-white"
                />
              )}
            </span>
            <span
              className={`mt-1.5 block text-[11.5px] sm:text-[12.5px] ${
                nu
                  ? "font-bold text-gray-900"
                  : gedaan
                    ? "text-[#333333]"
                    : "text-gray-500"
              }`}
            >
              {t(`volgen.stap_${naam}`)}
              <span className="sr-only">
                {" "}
                (
                {nu
                  ? t("volgen.stap_nu")
                  : gedaan
                    ? t("volgen.stap_gedaan")
                    : t("volgen.stap_nog_niet")}
                )
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function BestellingKaart({ bestelling }: { bestelling: VolgenBestelling }) {
  const { t } = useTranslation();
  const icoon = bestelling.geannuleerd ? Ban : STAP_ICOON[bestelling.stap];
  // Lightspeed blijft na bezorging "verzonden" zeggen; de stap weet
  // het beter, dus bij bezorgd wint het staplabel.
  const statusWoord =
    !bestelling.geannuleerd && bestelling.stap === "bezorgd"
      ? t("volgen.stap_bezorgd")
      : bestelling.status_tekst
        ? beginKapitaal(bestelling.status_tekst)
        : bestelling.geannuleerd
          ? t("volgen.geannuleerd")
          : t(`volgen.stap_${bestelling.stap}`);
  return (
    <section
      aria-label={t("volgen.bestelling_label", {
        ordernummer: bestelling.ordernummer,
      })}
      className={RETOUR_KAART}
    >
      <p className="text-[13.5px] text-gray-500">
        {t("volgen.bestelling")}{" "}
        <span className="font-bold text-gray-900">
          {bestelling.ordernummer}
        </span>
        {" · "}
        {t("volgen.geplaatst", { datum: dagTekst(bestelling.geplaatst_op) })}
      </p>
      <p
        className={`mt-3 flex items-center gap-2 text-[15px] font-bold ${
          bestelling.geannuleerd ? "text-gray-500" : "text-brand-navy"
        }`}
      >
        {createElement(icoon, {
          className: "h-5 w-5 shrink-0",
          "aria-hidden": true,
        })}
        <span data-testid="bestelling-status">{statusWoord}</span>
      </p>
      {bestelling.geannuleerd ? (
        <p className="mt-3 text-[14px]">{t("volgen.geannuleerd_tekst")}</p>
      ) : (
        <Voortgangslijn stap={bestelling.stap} />
      )}
    </section>
  );
}

function GeenZending({ stap }: { stap: VolgenStap }) {
  const { t } = useTranslation();
  const sleutel =
    stap === "besteld"
      ? "volgen.geen_zending_besteld"
      : stap === "ingepakt"
        ? "volgen.geen_zending_ingepakt"
        : "volgen.geen_zending_verzonden";
  return (
    <section className={`mt-4 ${RETOUR_KAART}`}>
      <p className="flex gap-2 text-[14px]">
        <Hourglass
          className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <span>{t(sleutel)}</span>
      </p>
    </section>
  );
}

// Regel in de zendingkaart: icoon plus tekst, zoals de vinkjeslijst
// van de retourpagina.
function ZendingRegel({
  icoon,
  children,
}: {
  icoon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <p className="flex items-start gap-2">
      {createElement(icoon, {
        className: "mt-0.5 h-4 w-4 shrink-0 text-muted-foreground",
        "aria-hidden": true,
      })}
      <span>{children}</span>
    </p>
  );
}

// Uitgelicht blok (bezorgd, verwachte bezorging): het lichtblauwe
// meldingsblok van de retourpagina.
function ZendingMelding({
  icoon,
  children,
}: {
  icoon: LucideIcon;
  children: React.ReactNode;
}) {
  return (
    <p className="mt-3 flex items-start gap-2 rounded-md border border-brand-cyan/40 bg-brand-cyan-soft p-3 text-[13px] font-bold">
      {createElement(icoon, {
        className: "mt-0.5 h-4 w-4 shrink-0 text-brand-cyan",
        "aria-hidden": true,
      })}
      <span>{children}</span>
    </p>
  );
}

function ZendingKaart({ zending }: { zending: VolgenZending }) {
  const { t } = useTranslation();
  const vervoerder = vervoerderNaam(zending.vervoerder);
  const bezorgd = zending.stand === "bezorgd";
  const verwacht = !bezorgd
    ? kalenderdagTekst(zending.verwachte_bezorgdag)
    : "";
  const verzonden = momentTekst(zending.verzonden_op);
  const scanMoment = momentTekst(zending.laatste_scan_op);
  const plaats = (zending.laatste_scan_plaats ?? "").trim();

  return (
    <section
      aria-label={
        vervoerder
          ? t("volgen.zending_label", { vervoerder })
          : t("volgen.zending")
      }
      className={`mt-4 ${RETOUR_KAART}`}
    >
      {/* Aantal buiten de kop: de kopletter (Arimo) is alleen vet
          geladen, dus "normaal" binnen de h2 werd op iOS vet. */}
      <div className="flex flex-wrap items-baseline">
        <h2 className="text-[16px] font-bold text-gray-900">
          {vervoerder ?? t("volgen.zending")}
        </h2>
        {zending.aantal_pakketten != null && (
          <span className="ml-1.5 text-[15px] text-gray-500">
            {"· "}
            {t("volgen.pakketten", { count: zending.aantal_pakketten })}
          </span>
        )}
      </div>
      <div className="mt-3 space-y-2.5 text-[13.5px] leading-snug text-[#333333]">
        {verzonden && (
          <ZendingRegel icoon={Send}>
            {t("volgen.verzonden", { moment: verzonden })}
          </ZendingRegel>
        )}
        {zending.stand === "nog_geen_scan" ? (
          <ZendingRegel icoon={Hourglass}>
            {t("volgen.nog_geen_scan")}
          </ZendingRegel>
        ) : !scanMoment && zending.stand === "onderweg" ? (
          <ZendingRegel icoon={Truck}>
            {t("volgen.onderweg_zonder_scan")}
          </ZendingRegel>
        ) : (
          scanMoment && (
            <ZendingRegel icoon={MapPin}>
              {t("volgen.laatste_scan", { moment: scanMoment })}
              {plaats && ` · ${plaats}`}
            </ZendingRegel>
          )
        )}
        {zending.stand === "afwijking" && (
          <ZendingRegel icoon={TriangleAlert}>
            {zending.track_trace_url
              ? t("volgen.afwijking_met_link")
              : t("volgen.afwijking_zonder_link")}
          </ZendingRegel>
        )}
      </div>
      {bezorgd && (
        <ZendingMelding icoon={CircleCheck}>
          {zending.bezorgd_op
            ? t("volgen.bezorgd_op", {
                moment: momentTekst(zending.bezorgd_op),
              })
            : t("volgen.stap_bezorgd")}
        </ZendingMelding>
      )}
      {verwacht && (
        <ZendingMelding icoon={CalendarCheck}>
          {t("volgen.verwachte_bezorging", { dag: verwacht })}
        </ZendingMelding>
      )}
      {zending.track_trace_url && (
        <Button asChild className="mt-4 h-11 w-full text-[15px]">
          <a
            href={zending.track_trace_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {vervoerder
              ? t("volgen.volg_bij", { vervoerder })
              : t("volgen.volg_bij_vervoerder")}
            <ExternalLink className="h-4 w-4" aria-hidden />
            <span className="sr-only">{t("volgen.nieuw_venster")}</span>
          </a>
        </Button>
      )}
      {zending.track_trace_code &&
        !zending.track_trace_url &&
        zending.stand !== "bezorgd" && (
          <p className="mt-1.5 text-[12.5px] text-muted-foreground">
            {t("volgen.volgcode", { code: zending.track_trace_code })}
          </p>
        )}
    </section>
  );
}

function RetourKaartIngeklapt({ data }: { data: RetourStatusPaginaAntwoord }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const paneelId = useId();
  return (
    // Kaart van /retour zonder binnenmarge: de knop draagt de p-6.
    <div className="mt-4 rounded-xl border border-border bg-slate-50">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={paneelId}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 rounded-xl p-6 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span>
          <span className="block text-[15px] font-bold text-gray-900">
            {t("volgen.retour", { retournummer: data.retournummer })}
          </span>
          <span className="mt-1 flex items-center gap-1.5 text-[13.5px] text-muted-foreground">
            {createElement(retourIcoon(data), {
              className: "h-4 w-4 text-brand-cyan",
              "aria-hidden": true,
            })}
            <RetourStatusWoord data={data} />
          </span>
        </span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {open && (
        <div id={paneelId} className="border-t border-border px-6 pb-6">
          {data.fase === "gesloten" ? (
            <RetourGeslotenBlok data={data} />
          ) : (
            <Tijdlijn data={data} />
          )}
          {data.fase !== "gesloten" && data.artikelen.length > 0 && (
            <ArtikelenKaart data={data} kaal />
          )}
        </div>
      )}
    </div>
  );
}

// Zijkaart naast formulier en resultaat: dezelfde kaart als "Ons
// retourbeleid in het kort" op /retour, met de link naar /retour op de
// plek van de beleid-link.
function VolgenUitleg() {
  const { t } = useTranslation();
  return (
    <VinkjesKaart
      titel={t("volgen.uitleg_titel")}
      punten={[
        t("volgen.uitleg_pakket"),
        t("volgen.uitleg_bezorgdag"),
        t("volgen.uitleg_retour"),
        t("volgen.uitleg_gegevens"),
      ]}
    >
      <Link
        to="/Retour"
        className="mt-4 inline-block rounded-sm text-[13.5px] font-medium text-blue-600 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {t("volgen.retour_aanmelden")}
      </Link>
    </VinkjesKaart>
  );
}

function BestellingResultaat({ antwoord }: { antwoord: VolgenAntwoord }) {
  const bestelling = antwoord.bestelling;
  if (!bestelling) return null;
  return (
    <>
      <BestellingKaart bestelling={bestelling} />
      {!bestelling.geannuleerd &&
        (bestelling.zendingen.length > 0 ? (
          bestelling.zendingen.map((zending, index) => (
            <ZendingKaart key={index} zending={zending} />
          ))
        ) : (
          <GeenZending stap={bestelling.stap} />
        ))}
      {antwoord.retouren.map((retour) => (
        <RetourKaartIngeklapt key={retour.retournummer} data={retour} />
      ))}
    </>
  );
}

function RetourResultaat({ data }: { data: RetourStatusPaginaAntwoord }) {
  return (
    <>
      <RetourStatusKop data={data} />
      {data.fase !== "gesloten" && data.artikelen.length > 0 && (
        <ArtikelenKaart data={data} />
      )}
    </>
  );
}

// ── De pagina ─────────────────────────────────────────────────────

type Weergave = "laden" | "zoeken" | "resultaat" | "te_veel";

export function VolgenPagina() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [weergave, setWeergave] = useState<Weergave>(() =>
    token ? "laden" : leesMissers() >= MAX_MISSERS ? "te_veel" : "zoeken",
  );
  const [antwoord, setAntwoord] = useState<VolgenAntwoord | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const [nummer, setNummer] = useState("");
  const [email, setEmail] = useState("");
  const [bezig, setBezig] = useState(false);

  // Tokenlink uit de retourmail: zelfde dienst als de oude
  // retourstatuspagina. Ongeldig → zoekscherm met de bekende melding.
  useEffect(() => {
    if (!token) return;
    let actueel = true;
    (async () => {
      try {
        const status = await retourStatusApi.viaToken(token);
        if (!actueel) return;
        setAntwoord({
          soort: "retour",
          voornaam: status.voornaam,
          bestelling: null,
          retouren: [status],
        });
        setWeergave("resultaat");
      } catch {
        if (!actueel) return;
        setFout(t("retour.statuspagina.link_ongeldig"));
        setWeergave(leesMissers() >= MAX_MISSERS ? "te_veel" : "zoeken");
      }
    })();
    return () => {
      actueel = false;
    };
  }, [token, t]);

  const zoek = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const schoonNummer = nummer.trim();
      const schoonEmail = email.trim();
      if (!schoonNummer || !schoonEmail) {
        setFout(t("volgen.vul_beide"));
        return;
      }
      setBezig(true);
      setFout(null);
      try {
        const gevonden = await volgenApi.zoeken(schoonNummer, schoonEmail);
        // Een treffer zet de teller terug: typfouten van eerder tellen
        // niet mee voor de volgende zoektocht (recensie 134).
        wisMissers();
        setAntwoord(gevonden);
        setWeergave("resultaat");
        window.scrollTo({ top: 0 });
      } catch (err) {
        const status = foutStatus(err);
        if (status === 429) {
          setWeergave("te_veel");
        } else if (status === 404) {
          if (telMisser() >= MAX_MISSERS) {
            setWeergave("te_veel");
          } else {
            setFout(t("volgen.niet_gevonden"));
          }
        } else {
          setFout(t("retour.fout_server"));
        }
      } finally {
        setBezig(false);
      }
    },
    [nummer, email, t],
  );

  const andereZoeken = useCallback(() => {
    setAntwoord(null);
    setFout(null);
    if (token) setSearchParams({}, { replace: true });
    setWeergave(leesMissers() >= MAX_MISSERS ? "te_veel" : "zoeken");
    window.scrollTo({ top: 0 });
  }, [token, setSearchParams]);

  // Schermlezers horen de nieuwe stand: de focus gaat naar de kop van
  // het resultaat of de stand (recensie 134).
  useEffect(() => {
    if (weergave === "laden") return;
    const kop = document.querySelector<HTMLElement>("main h1");
    if (!kop || kop === document.activeElement) return;
    if (weergave === "zoeken" && !antwoord) return;
    kop.setAttribute("tabindex", "-1");
    // Focus voor de schermlezer, geen ring in beeld (de kop is geen knop).
    kop.style.outline = "none";
    kop.focus({ preventScroll: true });
  }, [weergave, antwoord]);

  const retour =
    antwoord?.soort === "retour" ? (antwoord.retouren[0] ?? null) : null;

  return (
    <div className="retour-shop flex min-h-screen flex-col bg-white">
      <RetourHeader />
      <UspBalk />

      <main className={`${RETOUR_KOLOM} flex-1 py-8`}>
        {weergave === "laden" && (
          <div
            className={`${RETOUR_KAART} flex items-center justify-center gap-2 text-[14px] text-muted-foreground`}
            role="status"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {t("volgen.laden")}
          </div>
        )}

        {weergave === "zoeken" && (
          <section aria-labelledby="volgen-titel">
            <h1
              id="volgen-titel"
              className="text-2xl font-bold tracking-tight text-brand-navy"
            >
              {t("volgen.titel")}
            </h1>
            <p className="mt-3 text-[14px]">{t("volgen.intro")}</p>
            {fout && (
              <div
                role="alert"
                aria-live="polite"
                className="mt-4 rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive"
              >
                {fout}
              </div>
            )}
            {/* Raster van /retour: formulier links, uitleg rechts;
                mobiel komt de uitleg onder het formulier. */}
            <div className={`mt-6 ${RETOUR_RASTER}`}>
              <div className="min-w-0">
                <form
                  onSubmit={zoek}
                  noValidate
                  className={`${RETOUR_KAART} space-y-4`}
                >
                  <div className="space-y-1.5">
                    <Label htmlFor="volgen-nummer">
                      {t("volgen.nummer_label")}
                    </Label>
                    <Input
                      id="volgen-nummer"
                      name="nummer"
                      type="text"
                      autoComplete="off"
                      autoCapitalize="characters"
                      spellCheck={false}
                      required
                      placeholder={t("volgen.nummer_placeholder")}
                      value={nummer}
                      onChange={(e) => setNummer(e.target.value)}
                      className="h-11 bg-white"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="volgen-email">
                      {t("volgen.email_label")}
                    </Label>
                    <Input
                      id="volgen-email"
                      name="email"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-11 bg-white"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="h-11 w-full text-[15px]"
                    disabled={bezig}
                  >
                    {bezig && (
                      <Loader2
                        className="mr-2 h-4 w-4 animate-spin"
                        aria-hidden
                      />
                    )}
                    {bezig ? t("volgen.bezig") : t("volgen.bekijken")}
                  </Button>
                  <p className="text-[12.5px] text-muted-foreground">
                    {t("volgen.hint")}
                  </p>
                </form>
              </div>
              <div className="mt-6 md:mt-0">
                <VolgenUitleg />
              </div>
            </div>
          </section>
        )}

        {weergave === "te_veel" && (
          <section aria-labelledby="volgen-te-veel">
            <h1
              id="volgen-te-veel"
              className="text-2xl font-bold tracking-tight text-brand-navy"
            >
              {t("volgen.te_veel_titel")}
            </h1>
            <p className="mt-3 text-[14px]">{t("volgen.te_veel_tekst")}</p>
            <div className="mt-4">
              <HulpStrook />
            </div>
          </section>
        )}

        {weergave === "resultaat" && antwoord && (
          <section aria-label={t("volgen.titel")}>
            {/* Bij een bestelling staat de kop boven het raster, zoals
                op /retour; bij een retour zit de kop in de statuskaart. */}
            {antwoord.soort === "bestelling" && antwoord.bestelling && (
              <h1 className="text-2xl font-bold tracking-tight text-brand-navy">
                {antwoord.voornaam
                  ? t("volgen.kop_hoi", { voornaam: antwoord.voornaam })
                  : t("volgen.kop_zonder_naam")}
              </h1>
            )}
            <div
              className={`${antwoord.soort === "bestelling" ? "mt-6 " : ""}${RETOUR_RASTER}`}
            >
              <div className="min-w-0">
                {antwoord.soort === "bestelling" ? (
                  <BestellingResultaat antwoord={antwoord} />
                ) : (
                  retour && <RetourResultaat data={retour} />
                )}
                <div className="mt-6 border-t border-border pt-3">
                  <button
                    type="button"
                    onClick={andereZoeken}
                    className={`text-[14px] ${LINK}`}
                  >
                    {antwoord?.soort === "retour"
                      ? t("volgen.andere_zoeken_retour")
                      : t("volgen.andere_zoeken")}
                  </button>
                </div>
              </div>
              <div className="mt-6 md:mt-0">
                <VolgenUitleg />
              </div>
            </div>
          </section>
        )}
      </main>

      <RetourFooter />
    </div>
  );
}

export default VolgenPagina;
